import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { formatTimeTo12Hour, getCurrentDateAndTime, GlobalService } from "../../../shared";
import { createAvaibility, getUserAvailabilities, UpdateUserAvailabilities, useAppDispatch } from "../../../../redux";
import { Edit } from "lucide-react";
import AvailabilityModal from "../../modals/AvailabilityModal";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import { EmergencyHoursModal } from "../../../shared/components";
import { RoleEnum } from "../../../shared/model";
import { useTranslation } from "react-i18next";
const defaultDays = {
    monday: { start: "", end: "", active: false },
    tuesday: { start: "", end: "", active: false },
    wednesday: { start: "", end: "", active: false },
    thursday: { start: "", end: "", active: false },
    friday: { start: "", end: "", active: false },
    saturday: { start: "", end: "", active: false },
    sunday: { start: "", end: "", active: false },
};
interface EmergencyAvailabilityProps {
    userId?: string;
    isModalOpen?: boolean;
    onModalOpen?: () => void;
    onModalClose?: () => void;
    showEmergencyHoursModal?: boolean;
    onEmergencyHoursModalOpen?: () => void;
    onEmergencyHoursModalClose?: () => void;
    onEmergencyHoursSaved?: () => void; // Callback to reset regular hours
}
export default function EmergencyAvailability({ 
    userId,
    isModalOpen: propIsModalOpen, 
    onModalOpen, 
    onModalClose,
    showEmergencyHoursModal,
    onEmergencyHoursModalOpen,
    onEmergencyHoursModalClose,
    onEmergencyHoursSaved,
    showEditIcon = true
}: EmergencyAvailabilityProps & { showEditIcon?: boolean }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useSelector((state: any) => state.auth);
    const { emergencyAvailabilities } = useSelector((state: any) => state.professional);
    const targetUserId = userId || user?.id; // Use passed userId or fallback to current user
    // const [showModal, setShowModal] = useState(false);
    const [localIsModalOpen, setLocalIsModalOpen] = useState(false);

    const [regularHours, setRegularHours] = useState(defaultDays);
    const [originalRegularHours, setOriginalRegularHours] = useState(defaultDays);
    const isControlled = propIsModalOpen !== undefined;
    const isModalOpen = isControlled ? propIsModalOpen : localIsModalOpen;

    const handleOpenModal = () => {
        if(Number(user?.role?.id) === RoleEnum.internationl_professional){
            GlobalService.showToast(
                "info",
                "",
                t("emergencyAvailability.internationalNotAllowed")
            );
            return;
        }
        // Check if emergency hours already exist
        if (emergencyAvailabilities && emergencyAvailabilities.length > 0) {
            // If emergency hours exist, directly open the availability modal
            if (isControlled) {
                onModalOpen?.();
            } else {
                setLocalIsModalOpen(true);
            }
        } else {
            // If no emergency hours exist, show the confirmation modal first
            if (onEmergencyHoursModalOpen) {
                onEmergencyHoursModalOpen();
            }
        }
    };

    const handleCloseModal = () => {
        if (isControlled) {
            onModalClose?.();
        } else {
            setLocalIsModalOpen(false);
        }
    };

    const handleEmergencyDataReset = () => {
        // Refresh the component data after emergency data is reset
        getUserAvailabilitiesFromServer();
    };

    useEffect(() => {
        if (emergencyAvailabilities && emergencyAvailabilities.length > 0) {
            const updated = { ...defaultDays };

            emergencyAvailabilities.forEach(item => {
                const day = item.day?.toLowerCase();
                if (updated[day]) {
                    updated[day] = {
                        id: item.id, // keep id if needed for updates
                        start: getCurrentDateAndTime(new Date(item.startAt)).substring(11, 16),
                        end: getCurrentDateAndTime(new Date(item.endAt)).substring(11, 16),
                        active: item.isAvailable,
                    };
                }
            });
            setOriginalRegularHours(updated);
            setRegularHours(updated); // or split based on `type` if you have both types
        } else {
            // Reset to default when no emergency availabilities exist
            setOriginalRegularHours(defaultDays);
            setRegularHours(defaultDays);
        }
    }, [emergencyAvailabilities]);

    useEffect(() => {
        getUserAvailabilitiesFromServer();
    }, [targetUserId]);

    const getUserAvailabilitiesFromServer = async () => {
        if (!targetUserId) return;
        await dispatch(getUserAvailabilities({ id: targetUserId }));
    };
    const handleSave = async (updatedHours: any) => {
        const newItems = [];
        let updatedCount = 0;
        let hasValidationError = false;

        // First validate all active days have both start and end times
        for (const day in updatedHours) {
            const updated = updatedHours[day];

            if (updated.active && (!updated.start || !updated.end)) {
                GlobalService.showToast(
                    "error",
                    t("emergencyAvailability.validationError"),
                    t("emergencyAvailability.setBothTimes", { day: day.charAt(0).toUpperCase() + day.slice(1) })
                );
                hasValidationError = true;
            }
        }

        if (hasValidationError) {
            return; // Don't proceed with saving if there are validation errors
        }

        for (const day in updatedHours) {
            const original = originalRegularHours[day];
            const updated = updatedHours[day];

            // Only continue if something has changed
            const hasChanged =
                original.start !== updated.start || original.end !== updated.end || original.active !== updated.active;

            if (!hasChanged) continue;

            // Skip if day is not active (we don't need to process inactive days)
            if (!updated.active) continue;

            const startAt = new Date();
            const endAt = new Date();

            const [startHour, startMin] = updated.start.split(":");
            const [endHour, endMin] = updated.end.split(":");
            startAt.setHours(+startHour, +startMin);
            endAt.setHours(+endHour, +endMin);

            const payloadItem = {
                isEmergency: true,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                userId: { id: user?.id },
                startAt: startAt.toISOString(),
                endAt: endAt.toISOString(),
                isAvailable: updated.active,
                day: day.charAt(0).toUpperCase() + day.slice(1),
            };

            if (updated.id) {
                // Update existing Emergency
                await dispatch(UpdateUserAvailabilities({ id: updated.id, data: payloadItem }));
                updatedCount++;
            } else {
                newItems.push(payloadItem);
            }
        }

        // Create new items in batch
        if (newItems.length > 0) {
            try {
                await dispatch(createAvaibility({ data: newItems }));
                GlobalService.showToast(
                    "success",
                    t("emergencyAvailability.success"),
                    newItems.length > 1
                        ? t("emergencyAvailability.newAvailabilitiesCreated")
                        : t("emergencyAvailability.newEmergencyCreated")
                );
            } catch (error) {
                GlobalService.showToast("error", t("emergencyAvailability.error"), t("emergencyAvailability.failedToCreate"));
            }
        }

        if (updatedCount > 0) {
            GlobalService.showToast(
                "success",
                t("emergencyAvailability.success"),
                updatedCount > 1 ? t("emergencyAvailability.availabilitiesUpdated") : t("emergencyAvailability.availabilityUpdated")
            );
        }

        if (newItems.length > 0 || updatedCount > 0) {
            handleCloseModal();
        }
    };
    return (
        <>
            <Card color={undefined} className="p-6 border-red-500">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold text-red-500">{t("emergencyAvailability.title")}</h2>
                    {showEditIcon && (
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" onClick={() => handleOpenModal()}>
                            <Edit className="w-4 h-4" />
                        </Button>
                    )}
                </div>
                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium mb-2">{t("emergencyAvailability.emergencyHours")}</h3>
                        <div className="grid grid-cols-1 gap-4">
                            {Object.entries(regularHours).map(
                                ([day, hours]) =>
                                    hours.active && (
                                        <div key={day} className="flex items-center justify-between">
                                            <span className="capitalize">{day}</span>
                                            {formatTimeTo12Hour(hours.start)} - {formatTimeTo12Hour(hours.end)}
                                        </div>
                                    )
                            )}
                        </div>
                    </div>
                </div>
            </Card>
            <AvailabilityModal
                isEmergency={true}
                open={isModalOpen}
                regularHours={regularHours}
                handleSave={handleSave}
                onClose={handleCloseModal}
                onEmergencyHoursSaved={onEmergencyHoursSaved}
                onEmergencyDataReset={handleEmergencyDataReset}
            />
            
            {/* Emergency Hours Modal */}
            {showEmergencyHoursModal && (
                <EmergencyHoursModal
                    isOpen={showEmergencyHoursModal}
                    onClose={onEmergencyHoursModalClose || (() => {})}
                    onConfirm={() => {
                        onEmergencyHoursModalClose?.();
                        if (isControlled) {
                            onModalOpen?.();
                        } else {
                            setLocalIsModalOpen(true);
                        }
                    }}
                />
            )}
        </>
    );
}
